import React, { CSSProperties } from "react";
import { containerMaxWidths, ContainerMaxWidth, justifyContentMap, alignItemsMap, shadows } from "../../../config/styleConfig";

interface ContainerExportProps {
  children?: React.ReactNode;
  background?: string;
  padding?: number;
  paddingX?: number;
  paddingY?: number;
  paddingLeft?: number;
  paddingRight?: number;
  margin?: number;
  marginTop?: number;
  maxWidth?: "full" | "screen-xl" | "screen-lg" | "screen-md";
  minHeight?: number;
  flexDirection?: "row" | "column";
  justifyContent?: "start" | "center" | "end" | "between" | "around";
  alignItems?: "start" | "center" | "end" | "stretch" | "baseline";
  gap?: number;
  mobileLayout?: "stack" | "scroll" | "wrap";
  mobilePaddingX?: number;
  mobilePaddingY?: number;
  borderRadius?: number;
  boxShadow?: "none" | "sm" | "md" | "lg" | "xl";
  overflow?: "visible" | "hidden" | "auto";
  width?: string;
  backgroundImage?: string;
  backgroundSize?: "cover" | "contain" | "auto";
  backgroundPosition?: string;
  disableResponsive?: boolean;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  order?: number;
  mobileOrder?: number;
  mobileAlignItems?: "start" | "center" | "end" | "stretch";
  mobileBackgroundPosition?: string;
  sectionId?: string;
}

export const ContainerExport: React.FC<ContainerExportProps> = ({
  children,
  background = "transparent",
  padding = 16,
  paddingX,
  paddingY,
  paddingLeft,
  paddingRight,
  margin = 0,
  marginTop,
  maxWidth = "full",
  minHeight = 0,
  flexDirection = "column",
  justifyContent = "start",
  alignItems = "stretch",
  gap = 0,
  mobileLayout = "stack",
  mobilePaddingX,
  mobilePaddingY,
  borderRadius = 0,
  boxShadow = "none",
  overflow,
  width,
  backgroundImage,
  backgroundSize = "cover",
  backgroundPosition = "center",
  disableResponsive = false,
  hideOnMobile = false,
  hideOnDesktop = false,
  order,
  mobileOrder,
  mobileAlignItems,
  mobileBackgroundPosition,
  sectionId,
}) => {
  const style: CSSProperties = {
    display: "flex",
    flexDirection,
    maxWidth: containerMaxWidths[(maxWidth || "full") as ContainerMaxWidth] || "100%",
    justifyContent: justifyContentMap[justifyContent || "start"] || (justifyContent as string),
    alignItems: alignItemsMap[alignItems || "stretch"] || (alignItems as string),
    paddingTop: `${paddingY ?? padding}px`,
    paddingBottom: `${paddingY ?? padding}px`,
    paddingLeft: `${paddingLeft ?? paddingX ?? padding}px`,
    paddingRight: `${paddingRight ?? paddingX ?? padding}px`,
    marginTop: `${marginTop ?? margin}px`,
    marginBottom: `${margin}px`,
    marginLeft: (maxWidth !== "full" || (width && width !== "100%" && width !== "auto")) ? "auto" : `${margin}px`,
    marginRight: (maxWidth !== "full" || (width && width !== "100%" && width !== "auto")) ? "auto" : `${margin}px`,
    minHeight: minHeight || "auto",
    gap: `${gap}px`,
    boxSizing: "border-box",
    width: width || "100%",
    ...(width ? { flex: "0 0 auto" } : {}),
    ...(borderRadius != null ? { borderRadius: `${borderRadius}px` } : {}),
    ...(boxShadow && boxShadow !== "none" ? { boxShadow: shadows[boxShadow as keyof typeof shadows] } : {}),
    ...(overflow && overflow !== "visible" ? { overflow } : {}),
    ...(order !== undefined ? { order } : {}),
    ...(backgroundImage
      ? {
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize,
          backgroundPosition,
          backgroundRepeat: "no-repeat",
        }
      : {}),
    ...(background?.startsWith("linear-gradient") || background?.startsWith("radial-gradient")
      ? { background }
      : { backgroundColor: background }),
  };

  const classNames = ["container-flex"];
  if (flexDirection === "row") {
    classNames.push("container-row");
    if (!disableResponsive) {
      classNames.push(`flex-container-${mobileLayout}`);
    }
  }
  if (width) {
    classNames.push("container-fixed-width");
  }

  return (
    <div
      style={style}
      className={classNames.join(" ")}
      {...(sectionId ? { id: sectionId } : {})}
      {...(hideOnMobile ? { "data-hide-on-mobile": "true" } : {})}
      {...(hideOnDesktop ? { "data-hide-on-desktop": "true" } : {})}
      {...(mobilePaddingX !== undefined ? { "data-mobile-padding-x": mobilePaddingX } : {})}
      {...(mobilePaddingY !== undefined ? { "data-mobile-padding-y": mobilePaddingY } : {})}
      {...(mobileAlignItems ? { "data-mobile-align-items": mobileAlignItems } : {})}
      {...(mobileOrder !== undefined ? { "data-mobile-order": mobileOrder } : {})}
      {...(mobileBackgroundPosition ? { "data-mobile-bg-position": mobileBackgroundPosition } : {})}
    >
      {children}
    </div>
  );
};
